import { useState, useEffect, useRef } from "react";
import { User, Mail, Shield, MapPin, Phone, Lock, Camera, Save, Loader2 } from "lucide-react";

interface UserProfile {
  name: string;
  email: string;
  role_name: string;
  phone: string;
  profile_image: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

const MyAccount = () => {
  const [profile, setProfile] = useState<UserProfile>({
    name: "", email: "", role_name: "", phone: "", profile_image: "",
    street_address: "", city: "", state: "", zip_code: "", country: ""
  });
  
  const [password, setPassword] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem("sevaa_admin_token");

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      // const response = await fetch("http://localhost/seva-backend/api/auth/profile.php", {
      const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/auth/profile.php", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setProfile({
          name: data.profile.name || "",
          email: data.profile.email || "",
          role_name: data.profile.role_name || "",
          phone: data.profile.phone || "",
          profile_image: data.profile.profile_image || "",
          street_address: data.profile.street_address || "",
          city: data.profile.city || "",
          state: data.profile.state || "",
          zip_code: data.profile.zip_code || "",
          country: data.profile.country || ""
        });
        if (data.profile.profile_image) {
          // setPreviewImage(`http://localhost/seva-backend${data.profile.profile_image}`);
          setPreviewImage(`https://www.sevaafoundation.com/seva-backend${data.profile.profile_image}`);
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ text: "", type: "" });

    const formData = new FormData();
    formData.append("name", profile.name);
    formData.append("email", profile.email);
    if (password) formData.append("password", password);
    formData.append("phone", profile.phone);
    formData.append("street_address", profile.street_address);
    formData.append("city", profile.city);
    formData.append("state", profile.state);
    formData.append("zip_code", profile.zip_code);
    formData.append("country", profile.country);
    
    // Agar existing image hai aur nayi change nahi hui to purani wali path bhej do
    if (!imageFile && profile.profile_image) {
        formData.append("existing_image", profile.profile_image);
    } else if (imageFile) {
        formData.append("profile_image", imageFile);
    }

    try {
      // const response = await fetch("http://localhost/seva-foundation/api/auth/profile.php", {
      const response = await fetch("https://www.sevaafoundation.com/seva-foundation/api/auth/profile.php", {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }, // FormData me Content-Type nahi dete, browser khud set karta hai
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        setMessage({ text: "Profile updated successfully!", type: "success" });
        setPassword(""); // Clear password field after save
      } else {
        setMessage({ text: data.error || "Failed to update profile", type: "error" });
      }
    } catch (error) {
      setMessage({ text: "Network error occurred", type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="p-8 text-center text-slate-500">Loading profile data...</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 bg-slate-50 min-h-screen font-sans">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white">
          <div>
            <h2 className="text-2xl font-bold">My Account</h2>
            <p className="text-slate-400 text-sm mt-1">Manage your personal information and security.</p>
          </div>
        </div>

        <div className="p-8">
          {message.text && (
            <div className={`mb-6 p-4 rounded-xl text-sm font-medium flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Profile Image Section */}
            <div className="flex items-center gap-6 pb-8 border-b border-slate-100">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-slate-100 border-4 border-white shadow-lg overflow-hidden flex items-center justify-center">
                  {previewImage ? (
                    <img src={previewImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User size={40} className="text-slate-400" />
                  )}
                </div>
                <button 
                  type="button" 
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 shadow-md transition-colors"
                >
                  <Camera size={16} />
                </button>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleImageChange} 
                  accept="image/*" 
                  className="hidden" 
                />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Profile Picture</h3>
                <p className="text-slate-500 text-sm">Upload a new avatar. Max size 2MB.</p>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name</label>
                <input type="text" required value={profile.name} onChange={(e) => setProfile({...profile, name: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" />
              </div>
             <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-3.5 text-slate-400" />
                  <input 
                    type="email" 
                    value={profile.email} 
                    onChange={(e) => setProfile({...profile, email: e.target.value})} 
                    className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" 
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Role (Read-only)</label>
                <div className="relative">
                  <Shield size={18} className="absolute left-4 top-3.5 text-slate-400" />
                  <input type="text" readOnly value={profile.role_name} className="w-full pl-11 pr-4 py-3 bg-slate-100 border border-slate-200 text-slate-500 rounded-xl outline-none cursor-not-allowed" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
                <div className="relative">
                  <Phone size={18} className="absolute left-4 top-3.5 text-slate-400" />
                  <input type="tel" value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} placeholder="+91 9876543210" className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div>
              <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2 border-b border-slate-100 pb-2">
                <MapPin size={20} className="text-orange-500" /> Address Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Street Address</label>
                  <input type="text" value={profile.street_address} onChange={(e) => setProfile({...profile, street_address: e.target.value})} placeholder="Flat No, Building, Area" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">City</label>
                  <input type="text" value={profile.city} onChange={(e) => setProfile({...profile, city: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">State / Province</label>
                  <input type="text" value={profile.state} onChange={(e) => setProfile({...profile, state: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">ZIP / Postal Code</label>
                  <input type="text" value={profile.zip_code} onChange={(e) => setProfile({...profile, zip_code: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Country</label>
                  <input type="text" value={profile.country} onChange={(e) => setProfile({...profile, country: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" />
                </div>
              </div>
            </div>

            {/* Security Section */}
            <div>
              <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2 border-b border-slate-100 pb-2">
                <Lock size={20} className="text-orange-500" /> Update Password
              </h3>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">New Password (Leave blank to keep current)</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full max-w-md px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-colors" />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-slate-100 flex justify-end">
              <button type="submit" disabled={isSaving} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg transition-all flex items-center gap-2">
                {isSaving ? <><Loader2 size={18} className="animate-spin" /> Saving...</> : <><Save size={18} /> Save Changes</>}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;